import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Link, useNavigate } from 'react-router-dom';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import {
    Badge,
    Box,
    Card,
    CardHeader,
    Chip, Fab, Grid,
    IconButton,
    Link as MuiLink, ListItem,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import { Add, Edit, Photo } from '@mui/icons-material';
import { typeCurrency, typeDate } from 'components/mui/Formatters';
import Date from 'components/formaters/Date';
import { VoyageModel, VoyageStatus } from 'models/VoyageModel';
import { CompanyType } from 'models/CompanyModel';
import useVoyageDocs from 'modules/voyages/hooks/useVoyageDocs';
import useVoyageModals from 'modules/voyages/hooks/useVoyageModals';
import useVoyages from 'modules/voyages/hooks/useVoyages';
import auth from 'recoil/atoms/auth';
import voyageFamily from 'recoil/atoms/voyageFamily';
import { VoyagesViewProps } from './VoyagesDesktop';


const departed = [VoyageStatus.Departed, VoyageStatus.Arrived];

const TableInner = ({status}: VoyagesViewProps) => {
    const {
        openAddVoyageModal,
        openEditVoyageModal,
        openCancelVoyageModal,
        //openLostVoyageModal,
        openVoyageNoteModal,
    } = useVoyageModals();

    const {
        downloadManifest,
        downloadBLs,
        downloadECs,
        downloadInvoices,
        downloadSI,
        downloadDismantledList,
        downloadDocPackage,
    } = useVoyageDocs();

    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const voyages = useRecoilValue(voyageFamily(status));

    const filtered = my.type === CompanyType.Shipper ? voyages.filter(v => v.cars.total) : voyages;

    const Row = ({data, index, style}: ListChildComponentProps) => {
        const navigate = useNavigate();
        const voyage = data[index] as VoyageModel;

        return (
            <Grid
                container
                style={style}
                key={index}
                sx={{
                    borderBottom: 1,
                    borderColor: 'grey.300'
                }}
                onClick={() => navigate(`/voyage/${voyage.id}`)}
            >
                <Grid
                    item
                    xs={12}

                    textAlign="center"
                >
                    {voyage.number}
                </Grid>
                <Grid item xs={6}>
                    {voyage.pol.name} <br/>
                    <small><Date date={voyage.departedAt || voyage.etd}/></small>
                </Grid>
                <Grid item xs={6} textAlign="right">
                    {voyage.pod.name} <br/>
                    <small><Date date={voyage.arrivedAt || voyage.eta}/></small>
                </Grid>


                <Grid item xs={4}>
                    {voyage.allowDismantled && (<>
                        <small>[R]</small><br/>
                        <Chip
                            label={voyage.cars.dis}
                            color="error"
                            variant="outlined"
                            size="small"
                        />
                    </>)}
                </Grid>

                <Grid item xs={4} textAlign="center">
                    <small>{t('cars')}</small><br/>
                    <Chip
                        label={my.type === CompanyType.Master ? `${voyage.cars.total}/${voyage.vessel.capacity}` : voyage.cars.total}
                        color={voyage.cars.total <= voyage.vessel.capacity ? 'success' : 'error'}
                        variant={voyage.cars.total ? undefined : 'outlined'}
                        size="small"
                    />
                </Grid>

                {my.permissions.has('invoices create') && (
                    <Grid item xs={4} textAlign="right">
                        <small>{t('paid_by_type')}</small><br/>
                        <Chip
                            sx={{cursor: 'pointer', mr: '4px'}}
                            label={voyage.cars.paidCaf}
                            color={voyage.cars.caf === 0 || voyage.cars.caf === voyage.cars.paidCaf ? 'success' : 'error'}
                            variant={voyage.cars.caf === 0 ? 'outlined' : undefined}
                            size="small"
                        />
                        <Chip
                            sx={{cursor: 'pointer'}}
                            label={voyage.cars.paidFob}
                            color={voyage.cars.fob === 0 || voyage.cars.fob === voyage.cars.paidFob ? 'success' : 'error'}
                            variant={voyage.cars.fob === 0 ? 'outlined' : undefined}
                            size="small"
                        />
                    </Grid>
                )}
            </Grid>
        )
    };

    return (
        <FixedSizeList
            height={window.innerHeight - 150}
            width="100%"
            itemSize={136}
            itemCount={filtered.length}
            itemData={filtered}
            overscanCount={5}
        >
            {Row}
        </FixedSizeList>
    );
};

const VoyagesDesktop = (p: VoyagesViewProps) => {
    return (
        <Suspense fallback={null}>
            <TableInner {...p}/>
        </Suspense>
    )
};

export default VoyagesDesktop;
